<template>
    <div>
        <b-input-group>
            <select2 ref="contactSelect"
                     :id="id"
                     :name="name"
                     :css-class="cssClass"
                     :state="state"
                     :disabled="(disabled || isUuidNullOrEmpty(clientId))"
                     :placeholder="placeholder"
                     :options="contacts"
                     enable-ajax
                     :multiple="multiple"
                     key-property="contactId"
                     text-property="name"
                     :in-b-modal="inBModal"
                     v-model="currentValue"
                     v-on:search="findContacts"
                     v-on:select="contactSelectionChanged"></select2>
            <b-input-group-append>
                <b-button variant="outline-secondary" v-b-tooltip :title="(contacts && contacts.length) ? 'Display Contact Details' : ''" :disabled="!(contacts && contacts.length)" v-on:click="contactDetailsOpen = true"><i class="fas fa-address-card"></i></b-button>
            </b-input-group-append>
        </b-input-group>
        <b-modal size="md" centered title="Contact Details" ok-only ok-title="Close" v-model="contactDetailsOpen">
            <b-list-group flush>
                <b-list-group-item v-for="contact in currentSelectedContacts" :key="contact.contactId">
                    <div class="font-weight-bold">{{ contact.name }}</div>
                    <div v-if="(contact.email != null)"><a :href="'mailto:' + contact.email">{{ contact.email }}</a></div>
                    <div v-if="(contact.phone != null)">{{ contact.phone }}</div>
                    <div v-if="(contact.address1 != null)">{{ contact.address1 + (contact.address2 ? (', ' + contact.address2) : '') }}</div>
                    <div v-if="($config.enableRegionStateOptions && contact.suburb != null)">{{ contact.suburb + (contact.state ? (' ' + contact.state) : '') + (contact.postcode ? (' ' + contact.postcode) : '') + (contact.country ? (' ' + contact.country) : '') }}</div>
                    <div v-if="(!$config.enableRegionStateOptions && contact.suburb != null)">{{ contact.suburb + (contact.postcode ? (' ' + contact.postcode) : '') + (contact.country ? (' ' + contact.country) : '') }}</div>
                </b-list-group-item>
            </b-list-group>
        </b-modal>
        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="lg" centered title="New Contact" v-model="newContactDialogShown" v-on:ok.prevent="passes(createContact)" v-on:hidden="newContactDialogHidden">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <b-form-group label="Title"
                                  label-for="contactTitle"
                                  class="col-md-2">
                        <b-form-input name="contactTitle"
                                      autofocus
                                      v-model="newContact.title">
                        </b-form-input>
                    </b-form-group>
                    <validation-provider :rules="`${!newContact.lastName ? 'required' : ''}`" name="Given Names" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Given Names"
                                      label-for="contactGivenNames"
                                      class="col-md-5">
                            <b-form-input name="contactGivenNames"
                                          v-model="newContact.givenNames"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider :rules="`${!newContact.givenNames ? 'required' : ''}`" name="Last Name" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Last Name"
                                      label-for="contactLastName"
                                      class="col-md-5">
                            <b-form-input name="contactLastName"
                                          v-model="newContact.lastName"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
                <b-form-row>
                    <validation-provider rules="required|email" name="Email" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Email"
                                      label-for="contactEmail"
                                      class="col-md-6">
                            <b-form-input name="contactEmail"
                                          v-model="newContact.email"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider :rules="`${isContactPropertyMandatory('phone') ? 'required' : ''}`" name="Phone" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Phone"
                                      label-for="contactPhone"
                                      class="col-md-6">
                            <vue-tel-input name="contactPhone"
                                           v-model="newContact.phone"
                                           :defaultCountry="defaultCountryCode"
                                           :mode="phoneMode"
                                           :onlyCountries="countryList"
                                           :preferredCountries="preferredCountyList"
                                           v-on:country-changed="phoneCountryChanged"
                                           :state="errors[0] ? false : null"
                                           class="form-control d-flex">
                            </vue-tel-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider :rules="`${isContactPropertyMandatory('address') ? 'required' : ''}`" name="Address" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Address"
                                      label-for="contactAddress1"
                                      class="col-12">
                            <b-form-input name="contactAddress1"
                                          v-model="newContact.address1"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <b-form-group class="col-12">
                        <b-form-input name="contactAddress2"
                                      v-model="newContact.address2">
                        </b-form-input>
                    </b-form-group>
                    <validation-provider :rules="`${isContactPropertyMandatory('suburb') ? 'required' : ''}`" name="Suburb" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Suburb"
                                      label-for="contactSuburb"
                                      class="col-md-4">
                            <b-form-input name="contactSuburb"
                                          v-model="newContact.suburb"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider v-if="$config.enableRegionStateOptions"
                                         :rules="`${isContactPropertyMandatory('state') ? 'required' : ''}`" name="State" v-slot="{ valid, errors }" slim>
                        <b-form-group v-if="$config.enableRegionStateOptions"
                                      label="State"
                                      label-for="contactState"
                                      class="col-md-2">
                            <select2 name="contactState"
                                     v-model="newContact.state"
                                     :options="states"
                                     :state="errors[0] ? false : null"
                                     tags
                                     in-b-modal>
                            </select2>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider :rules="`${isContactPropertyMandatory('postcode') ? 'required' : ''}`" name="Postcode" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Postcode"
                                      label-for="contactPostcode"
                                      class="col-md-2">
                            <b-form-input name="contactPostcode"
                                          v-model="newContact.postcode"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider :rules="`${isContactPropertyMandatory('country') ? 'required' : ''}`" name="Country" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Country"
                                      label-for="contactCountry"
                                      class="col-md-4">
                            <b-form-input name="contactCountry"
                                          v-model="newContact.country"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
            </b-modal>
        </validation-observer>
    </div>
</template>

<script>
    import $ from 'jquery';
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';
    
    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required, email } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('email', email);
    localize('en', en);

    import VueTelInput from 'vue-tel-input';
    import 'vue-tel-input/dist/vue-tel-input.css';

    import ErrorsMixin from '../mixins/ErrorsMixin';
    import UuidMixins from '../mixins/UuidMixins';
    import Select2 from './Select2';

    Vue.use(BootstrapVue);
    Vue.use(VueTelInput);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('select2', Select2);

    export default {
        name: 'ContactSelect',
        mixins: [ErrorsMixin, UuidMixins],
        model: {
            event: 'change',
            prop: 'value'
        },
        props: {
            id: {
                type: String,
                default: ''
            },
            name: {
                type: String,
                default: ''
            },
            apiUrl: {
                type: String,
                required: true
            },
            refDataApiControllerUrl: {
                type: String,
                required: true
            },
            clientId: {
                type: String,
                default: null
            },
            disabled: {
                type: Boolean,
                default: false
            },
            multiple: {
                type: Boolean,
                default: false
            },
            cssClass: {
                type: String,
                default: 'form-control'
            },
            state: {
                type: Boolean,
                default: null
            },
            initialContacts: {
                type: Array,
                default: null
            },
            mandatoryContactProperties: {
                type: Array,
                default: null
            },
            inBModal: {
                type: Boolean,
                default: false
            },
            value: null
        },
        data: function () {
            return {
                states: [],
                initialCountry: null,
                contacts: this.initialContacts,
                currentValue: this.value,
                currentSelectedContacts: null,
                newContact: { title: null, givenNames: null, lastName: null, email: null, phone: null, address1: null, address2: null, postcode: null, suburb: null, state: null, country: null },
                currentErrors: [],
                newContactDialogShown: false,
                contactDetailsOpen: false,
                countryList: [],
                preferredCountyList: [],
                defaultCountryCode: null,
            }
        },
        watch: {
            clientId: function () {
                this.contacts = null;
                if (this.currentValue != null) {
                    this.currentValue = null;
                    this.currentSelectedContacts = null;
                    this.$emit('change', this.currentValue);
                }
            }
        },
        computed: {

            placeholder: function () {
                if (this.isUuidNullOrEmpty(this.clientId)) {
                    return 'Select a client first';
                }
                if (this.multiple) {
                    return '';
                }
                return 'Select a contact';
            },
            showErrors: function () {
                return this.currentErrors.length > 0;
            },
            phoneMode: function () {    
                if (!this.newContact.id) {
                    return "international";
                }
                else if (!this.newContact.phone || this.newContact.phone == '' || !this.newContact.phone?.startsWith("0")) {
                    return "international";
                }
                else {
                    return "national";
                }
            },
        },
        methods: {
            getSelectedContacts: function () {
                return this.currentSelectedContacts;
            },
            isContactPropertyMandatory: function (propertyName) {
                if (this.mandatoryContactProperties == null) return false;
                return (this.mandatoryContactProperties.indexOf(propertyName) >= 0);
            },
            findContacts: function(params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;
                var url = this.apiUrl + '/' + this.clientId + '/find';
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = $.map(mappedData, function (obj) {
                                obj.id = obj.contactId;
                                obj.text = obj.name;
                                return obj;
                            });
                            mappedData[mappedData.length] = { id: '00000000-0000-0000-0000-000000000000', text: 'New Contact' };
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            contactSelectionChanged: function() {
                if ((!this.multiple && (this.currentValue == '00000000-0000-0000-0000-000000000000')) ||
                    (this.multiple && (this.currentValue.includes('00000000-0000-0000-0000-000000000000')))) {
                    this.newContact = { title: null, givenNames: null, lastName: null, email: null, phone: null, address1: null, address2: null, postcode: null, suburb: null, state: null, country: this.initialCountry };
                    this.currentErrors = [];
                    this.newContactDialogShown = true;
                } else {
                    this.currentSelectedContacts = this.$refs.contactSelect.getSelectedData();
                    this.$emit('change', this.currentValue);
                }
            },
            createContact: function() {
                var data = {
                    clientId: this.clientId,
                    ...this.newContact
                };
                this.currentErrors = [];
                var url = this.apiUrl;
                axios
                    .post(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(response => {
                        var newContact = response.data;
                        if (!this.multiple) {
                            this.$refs.contactSelect.setOptionsAndValue([newContact], newContact.contactId);
                            this.currentValue = newContact.contactId;
                        } else {
                            var currentSelectedContacts = this.currentValue;
                            if (currentSelectedContacts) {
                                if (currentSelectedContacts.includes('00000000-0000-0000-0000-000000000000')) {
                                    var newEntryIndex = currentSelectedContacts.indexOf('00000000-0000-0000-0000-000000000000');
                                    currentSelectedContacts.splice(newEntryIndex, 1);
                                }
                                currentSelectedContacts.push(newContact.contactId);
                            } else {
                                currentSelectedContacts = [newContact.contactId];
                            }
                            var contacts = this.currentSelectedContacts;
                            if (contacts) {
                                contacts.push(newContact);
                            } else {
                                contacts = [newContact];
                            }
                            this.$refs.contactSelect.setOptionsAndValue(contacts, currentSelectedContacts);
                            this.currentValue = currentSelectedContacts;
                        }
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length == 0) {
                            this.newContactDialogShown = false;
                        }
                    });
            },
            newContactDialogHidden: function() {
                if (!this.multiple && (this.currentValue == '00000000-0000-0000-0000-000000000000')) {
                    this.currentValue = null;
                } else if (this.multiple && (this.currentValue.includes('00000000-0000-0000-0000-000000000000'))) {
                    var currentSelectedContacts = this.currentValue;
                    var newEntryIndex = currentSelectedContacts.indexOf('00000000-0000-0000-0000-000000000000');
                    currentSelectedContacts.splice(newEntryIndex, 1);
                    this.currentValue = currentSelectedContacts;
                }
                this.contactSelectionChanged();
                this.$refs.contactSelect.focus();
            },
            phoneCountryChanged: function (country) {
                var selectedCountryCode = country.iso2.toLowerCase();
                var defaultCountryCode = this.defaultCountryCode.toLowerCase();

                if (this.currentContact.phone?.startsWith("0") && selectedCountryCode != defaultCountryCode) {
                    this.currentContact.phone = '+' + country.dialCode + ' ' + this.currentContact.phone.substring(1);
                }
            },
            reset: function () {
                this.contacts = this.initialContacts;
                this.currentValue = this.value;
                Vue.nextTick(() => {
                    if (this.currentValue != null) this.currentSelectedContacts = this.$refs.contactSelect.getSelectedData();
                });
            }
        },
        created: function () {
            var url = this.refDataApiControllerUrl;
            if (this.$config.enableRegionStateOptions) {
                if (!url.endsWith("/"))
                    url += "/";
                url += "list/Contact.State.Choices"
                axios
                    .get(url)
                    .then(response => {
                        this.states = response.data
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            }
            url = this.refDataApiControllerUrl;
            if (!url.endsWith("/"))
                url += "/";
            url += "value/Contact.Country.InitialValue"
            axios
                .get(url)
                .then(response => {
                    this.initialCountry = response.data
                })
                .catch(ex => {
                    var currentErrors = this.handleAxiosException(ex);
                    this.showErrorsToast(currentErrors);
                });

            url = this.refDataApiControllerUrl;
            if (!url.endsWith("/"))
                url += "/";
            url += "list/Contact.CountryCode.Choices"
            axios
                .get(url)
                .then(response => {
                    response.data.forEach(obj => (this.countryList.push(obj.value)));
                })
                .catch(ex => {
                    var currentErrors = this.handleAxiosException(ex);
                    this.showErrorsToast(currentErrors);
                });

            url = this.refDataApiControllerUrl;
            if (!url.endsWith("/"))
                url += "/";
            url += "list/Contact.PreferredCountryCode.Choices"
            axios
                .get(url)
                .then(response => {
                    response.data.forEach(obj => (this.preferredCountyList.push(obj.value)));
                })
                .catch(ex => {
                    var currentErrors = this.handleAxiosException(ex);
                    this.showErrorsToast(currentErrors);
                });

            url = this.refDataApiControllerUrl;
            if (!url.endsWith("/"))
                url += "/";
            url += "value/Contact.CountryCode.DefaultValue"
            axios
                .get(url)
                .then(response => {
                    this.defaultCountryCode = response.data
                })
                .catch(ex => {
                    var currentErrors = this.handleAxiosException(ex);
                    this.showErrorsToast(currentErrors);
                });
        },
        mounted: function () {
            if (this.currentValue != null) this.currentSelectedContacts = this.$refs.contactSelect.getSelectedData();
        }
    }
</script>